// src/App.js

import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocaleProvider } from './shared/contexts/LocaleContext';
import Home from './modules/home/pages/HomePage';
import Plan from './modules/menu/pages/PlanPage';
import MenuView from './modules/menu/pages/MenuViewPage';
import Now from './modules/meal/pages/NowPage';
import ShoppingList from './modules/shopping-list/pages/ShoppingListPage';
import RecipeLibrary from './modules/meal/pages/RecipeLibraryPage';
import MealDetails from './modules/meal/pages/MealDetailsPage';
import LoginForm from './modules/auth/pages/LoginPage';
import RegisterForm from './modules/auth/pages/RegisterPage';
import PrivacyPolicyPage from './modules/legal/pages/PrivacyPolicyPage';
import CookiesPolicyPage from './modules/legal/pages/CookiesPolicyPage';
import PricingPage from './modules/pricing/pages/PricingPage';
import PaymentSuccessPage from './modules/payment/pages/PaymentSuccessPage';
import FeedbackButton from './shared/components/FeedbackButton';
import ErrorBoundary from './shared/components/ErrorBoundary';
import authService from './modules/auth/services/authService';


function ProtectedRoute({ children }) {
    const { t } = useTranslation();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const intervalRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        
        const checkAuth = () => {
            if (!mounted) return;
            
            const auth = authService.isAuthenticated();
            
            // Si el usuario ya no está autenticado, detener el polling
            if (!auth && intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            
            setIsAuthenticated(prev => {
                // Solo actualizar si cambió el estado para evitar re-renders innecesarios
                if (prev !== auth) {
                    return auth;
                }
                return prev;
            });
            
            if (mounted) {
                setIsLoading(false);
            }
        };

        // Verificación inicial
        checkAuth();
        
        // Solo iniciar el polling si el usuario está autenticado
        if (authService.isAuthenticated()) {
            intervalRef.current = setInterval(checkAuth, 1000);
        }
        
        return () => {
            mounted = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="text-lg text-gray-600">{t('common.loading')}</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    return children;
}

function App() {
    return (
        <ErrorBoundary>
            <LocaleProvider>
                <Router>
                    <div className="min-h-screen bg-gray-50">
                        <Routes>
                            <Route path="/" element={authService.isAuthenticated() ? <Navigate to="/now" replace /> : <Home />} />
                            <Route path="/login" element={<LoginForm />} />
                            <Route path="/register" element={<RegisterForm />} />
                            <Route path="/pricing" element={<PricingPage />} />
                            <Route path="/payment-successful" element={<PaymentSuccessPage />} />
                            <Route path="/privacy" element={<PrivacyPolicyPage />} />
                            <Route path="/cookies" element={<CookiesPolicyPage />} />
                            <Route
                                path="/plan"
                                element={
                                    <ProtectedRoute>
                                        <Plan />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/plan/:date"
                                element={
                                    <ProtectedRoute>
                                        <MenuView />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/now"
                                element={
                                    <ProtectedRoute>
                                        <Now />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/shopping-list"
                                element={
                                    <ProtectedRoute>
                                        <ShoppingList />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/recipes"
                                element={
                                    <ProtectedRoute>
                                        <RecipeLibrary />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/recipe/:id"
                                element={
                                    <ProtectedRoute>
                                        <MealDetails />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Redirigir cualquier ruta no encontrada a home */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                        <FeedbackButton />
                    </div>
                </Router>
            </LocaleProvider>
        </ErrorBoundary>
    );
}

export default App;